// EmptyState — the one "there's nothing here" surface (DESIGN-SYSTEM.md §7).
//
// The partner of the tab-handle standard: a handle whose panel is empty stays
// live and unfaded precisely BECAUSE landing on it teaches you what the thing
// is and how to make one. That promise only pays off if the panel says
// something useful, so an empty state is a `title` plus — wherever the user
// could act — a `hint` and an `action`. A bare sentence is the floor, not the
// target.
//
// Two variants, by the space it fills:
//   list  — inside a nav list / rail panel. Compact, left-aligned, muted.
//   panel — a whole pane or page. Centered box, larger type, room for an action.
import type { ReactNode } from "react";
import { cx } from "./classNames.js";

export type EmptyStateVariant = "list" | "panel";

export interface EmptyStateProps {
  /** The one-line statement of what's missing. */
  readonly title: ReactNode;
  /** Optional teaching line — how to create the thing, or why it's empty. */
  readonly hint?: ReactNode;
  /** Optional call to action (a `Button`). */
  readonly action?: ReactNode;
  readonly variant?: EmptyStateVariant;
  readonly className?: string;
}

export function EmptyState({
  title,
  hint,
  action,
  variant = "list",
  className,
}: EmptyStateProps) {
  if (variant === "panel") {
    return (
      <div
        className={cx(
          "rounded-xl border border-slate-800 bg-slate-900 p-12 text-center",
          className,
        )}
      >
        <h2 className="mb-2 text-lg font-semibold text-slate-100">{title}</h2>
        {hint && <p className="mx-auto mb-6 max-w-prose text-sm text-slate-400">{hint}</p>}
        {action}
      </div>
    );
  }
  return (
    <div className={cx("px-3 py-2 text-slate-500", className)}>
      <p>{title}</p>
      {hint && <p className="mt-1 text-slate-600">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
